import React, { useEffect, useState, useCallback } from 'react';
import { adminApi } from '../../lib/adminApi';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';

const inputCls = 'bg-slate-950 border-white/10 text-white placeholder:text-slate-500 rounded-xl';

export const FaqManager = ({ onAuthError }) => {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => adminApi.get('/faq/all').then(({ data }) => setItems(data.data)).catch(onAuthError), [onAuthError]);
  useEffect(() => { load(); }, [load]);

  const save = async (form) => {
    setSaving(true);
    try {
      if (form.id) await adminApi.put(`/faq/${form.id}`, form); else await adminApi.post('/faq', form);
      toast({ title: form.id ? 'Pregunta actualizada' : 'Pregunta creada' });
      setEditing(null); load();
    } catch (err) { if (!onAuthError(err)) toast({ title: 'Error', description: 'No se pudo guardar.', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`¿Eliminar la pregunta "${f.pregunta}"?`)) return;
    await adminApi.delete(`/faq/${f.id}`).catch(onAuthError);
    toast({ title: 'Pregunta eliminada' }); load();
  };

  const move = async (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    await adminApi.put('/faq/reorder', { ids: next.map((f) => f.id) }).catch(onAuthError);
  };

  return (
    <section className="bg-white/5 border border-white/10 rounded-3xl p-6" data-testid="admin-faq">
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="text-lg font-semibold">Preguntas frecuentes</h2><p className="text-slate-400 text-sm">Se muestran desplegables en la página. Usa las flechas para ordenar.</p></div>
        <Button data-testid="faq-add-btn" onClick={() => setEditing({ pregunta: '', respuesta: '', activo: true })} className="rounded-xl bg-blue-600 hover:bg-blue-500"><Plus className="h-4 w-4 mr-2" /> Nueva</Button>
      </div>
      <div className="space-y-2">
        {items.map((f, i) => (
          <div key={f.id} data-testid="faq-admin-row" className={`flex items-start gap-3 rounded-2xl border p-4 ${f.activo ? 'border-white/10 bg-slate-900/50' : 'border-dashed border-white/10 opacity-60'}`}>
            <div className="flex flex-col gap-1 pt-0.5">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-white disabled:opacity-30" aria-label="Subir" data-testid="faq-move-up"><ArrowUp className="h-4 w-4" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-slate-400 hover:text-white disabled:opacity-30" aria-label="Bajar" data-testid="faq-move-down"><ArrowDown className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold">{f.pregunta}</p>
              <p className="text-sm text-slate-400 line-clamp-2">{f.respuesta}</p>
            </div>
            <Badge className={`border-0 shrink-0 ${f.activo ? 'bg-emerald-500/20 text-emerald-300' : 'bg-slate-500/20 text-slate-300'}`}>{f.activo ? 'Visible' : 'Oculta'}</Badge>
            <div className="flex gap-1 shrink-0">
              <Button size="sm" variant="ghost" onClick={() => setEditing(f)} className="h-8 px-2 hover:bg-white/10 hover:text-white" data-testid="faq-edit-btn"><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => save({ ...f, activo: !f.activo })} className="h-8 px-2 hover:bg-white/10 hover:text-white" data-testid="faq-toggle-btn">{f.activo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(f)} className="h-8 px-2 text-red-300 hover:bg-red-500/10 hover:text-red-200" data-testid="faq-delete-btn"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-slate-900 border-white/10 text-white sm:max-w-lg" data-testid="faq-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? 'Editar pregunta' : 'Nueva pregunta'}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={(e) => { e.preventDefault(); save(editing); }} className="space-y-4">
              <Input data-testid="faq-pregunta-input" required minLength={5} maxLength={200} value={editing.pregunta} onChange={(e) => setEditing((f) => ({ ...f, pregunta: e.target.value }))} className={inputCls} placeholder="Pregunta" />
              <Textarea data-testid="faq-respuesta-input" required minLength={5} maxLength={2000} rows={5} value={editing.respuesta} onChange={(e) => setEditing((f) => ({ ...f, respuesta: e.target.value }))} className={inputCls} placeholder="Respuesta" />
              <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={!!editing.activo} onChange={(e) => setEditing((f) => ({ ...f, activo: e.target.checked }))} data-testid="faq-activo-checkbox" /> Visible en la página</label>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)} className="rounded-xl border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">Cancelar</Button>
                <Button type="submit" disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-500" data-testid="faq-save-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default FaqManager;
